import queryClient from '@src/app/providers/queryClient';
import type {
  Task,
  TaskCreate,
  TaskCreateDto,
  TaskDto,
  TaskComplete,
  TaskPriorityChoicesResponse,
  TaskReassign,
  TaskReassignDto,
  TaskStatusChoicesResponse,
  TaskTypeChoice,
  TaskTypeChoicesResponseDto,
  TaskSummaryResponse,
  TaskUpdate,
  TaskUpdateDto,
  TasksResponse,
} from '@src/features/tasks';
import {
  taskCreateToDto,
  taskFromDto,
  taskTypeChoiceFromDto,
  taskUpdateToDto,
} from '@src/features/tasks/lib/task';
import useAPIClient from '@src/shared/hooks/api/useAPIClient';
import { useMutation, useQuery } from '@src/shared/query';
import { type InfiniteData, keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';

import { tasksUrl } from './constants';

const TASK_SUMMARY_REFETCH_INTERVAL = 5 * 60 * 1000;

export type UseTasksParams = {
  filterParams?: Record<string, unknown>;
  pageSize: number;
  ordering?: string;
};

export type TasksListResponse = Omit<TasksResponse, 'results'> & {
  results: Task[];
};

export type UpdateTaskPayload = {
  id: number | string;
  payload: Partial<TaskUpdate>;
};

export function useTasks({
  filterParams = {},
  pageSize,
  ordering,
}: UseTasksParams) {
  const apiClient = useAPIClient();

  return useInfiniteQuery({
    queryKey: ['root', 'tasks', pageSize, ordering, filterParams],
    initialPageParam: 1,
    placeholderData: keepPreviousData,
    queryFn: ({ pageParam, signal }) =>
      apiClient.request({
        url: tasksUrl,
        payload: {
          page: String(pageParam),
          page_size: String(pageSize),
          ...filterParams,
          ...(ordering ? { ordering } : {}),
        },
        options: { signal },
      }) as Promise<TasksResponse>,
    select: (data): InfiniteData<TasksListResponse> => ({
      ...data,
      pages: (data.pages ?? []).map((page) => ({
        ...page,
        results: page.results.map(taskFromDto),
      })),
    }),
    getNextPageParam: (lastPage) => {
      if (!lastPage.meta.next) {
        return undefined;
      }
      return lastPage.meta.page + 1;
    },
  });
}

export function useTaskTypes(searchQuery = '') {
  return useQuery<TaskTypeChoicesResponseDto, TaskTypeChoice[]>('tasks/types/', {
    initialData: { results: [] },
    queryParams: {
      page_size: '500',
      ...(searchQuery ? { q: searchQuery } : {}),
    },
    select: (data) => data.results.map(taskTypeChoiceFromDto),
  });
}

export function useTaskStatusChoices() {
  return useQuery<TaskStatusChoicesResponse>('tasks/choices/task-statuses/', {
    initialData: {},
  });
}

export function useTaskPriorityChoices() {
  return useQuery<TaskPriorityChoicesResponse>(
    'tasks/choices/task-priorities/',
    {
      initialData: {},
    },
  );
}

export function useTaskSummary() {
  return useQuery<TaskSummaryResponse>('tasks/summary/', {
    initialData: {
      overdue_tasks: 0,
      due_tasks: 0,
      pending_tasks: 0,
    },
    refetchInterval: TASK_SUMMARY_REFETCH_INTERVAL,
  });
}

export function useUpdateTask() {
  return useMutation<TaskDto, Error, UpdateTaskPayload>({
    mutationKey: ['root', 'tasks', 'update'],
    mutateFn: ({ apiClient, id, payload }) =>
      apiClient.request({
        url: `${tasksUrl}${id}/`,
        method: 'PATCH',
        payload: taskUpdateToDto(payload) as Partial<TaskUpdateDto>,
      }) as Promise<TaskDto>,
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['root', 'tasks'],
      }),
  });
}

export function useCreateTask() {
  return useMutation<TaskDto, Error, TaskCreate>({
    mutationKey: ['root', 'tasks', 'create'],
    mutateFn: ({ apiClient, ...payload }) =>
      apiClient.request({
        url: tasksUrl,
        method: 'POST',
        payload: taskCreateToDto(payload) as TaskCreateDto,
      }) as Promise<TaskDto>,
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['root', 'tasks'],
      }),
  });
}

export function useReassignTask() {
  return useMutation<TaskDto, Error, TaskReassign>({
    mutationKey: ['root', 'tasks', 'reassign'],
    mutateFn: ({ apiClient, id, taskUserId }) =>
      apiClient.request({
        url: `${tasksUrl}${id}/reassign/`,
        method: 'POST',
        payload: {
          task_user: taskUserId,
        } satisfies TaskReassignDto,
      }) as Promise<TaskDto>,
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['root', 'tasks'],
      }),
  });
}

export function useCompleteTask() {
  return useMutation<TaskDto, Error, TaskComplete>({
    mutationKey: ['root', 'tasks', 'complete'],
    mutateFn: ({ apiClient, id }) =>
      apiClient.request({
        url: `${tasksUrl}${id}/complete/`,
        method: 'POST',
      }) as Promise<TaskDto>,
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['root', 'tasks'],
      }),
  });
}
